/**
 * Root Orchestrator
 *
 * This file delegates to product-specific root components based on PRODUCT_NAME env var.
 * Supports multiple products (narcis, quies, etc.) from a single codebase.
 */

import {json} from '@core/lib/json'
import {type LinksFunction, type LoaderFunctionArgs, type MetaFunction} from 'react-router'
import {isRouteErrorResponse, Link, Links, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useLocation, useRouteError} from 'react-router'
import {type PropsWithChildren, lazy, Suspense, useEffect, useState} from 'react'
import {trackPageView, track, markInternalTraffic, setAnalyticsIdentity, setBrowserSessionId, setVisitorState} from '@core/utils/analytics'
import {useBehavioralTracking} from '@core/utils/analytics.behavioral'
import {GATE_TRIGGER_WALL, GATE_TRIGGER_SHARE} from '@core/wall/contract'
import {GATE_TRIGGER_ONE_TAP} from '@core/auth/signInMethods'
import {routerPaths} from '../routes'
import {getProductConfig, getProductId} from './core/products/registry.js'
import {toPublicConfig} from './core/products/publicConfig.js'
import {GoogleOneTap} from './core/auth-ui/GoogleOneTap'
// Product styles — all imported statically into ONE bundle; build-time product only
// selects which routes ship, not which CSS. So every product's index.scss is present in
// every build. IMPORTANT: narcis/styles ships unscoped bare element rules (body font,
// h1–h4/p spacing) that bleed across products; ovid scopes its own rules + element resets
// under [data-product="ovid"] to win the cascade. Quies/ovid carry their own scoping.
import '@product/narcis/styles/index.scss'
import '@product/quies/styles/index.scss'
import '@product/ovid/styles/index.scss'
import '@product/toile/styles/index.scss'
// Ovid's 18+ entry overlay (plan 2026_08_05_ovid_age_gate.md, step 4). `React.lazy` since
// 2026-09-17: this overlay's copy is the ONE hit the anonymous chunk scan reports
// (`scripts/wall/scan_anon_chunks.ts` — the word "explicit" in its own paragraph), and it has been
// in the root chunk since the overlay shipped (08eb3c1f4, 2026-08-05), long before any wall. It is
// parked: `OVID_AGE_OVERLAY_AT_ENTRY` is off unless the env turns it on (plan
// 2026_09_17_ovid_veiled_wall.md step 7), so `showAgeGate` is false and the branch
// below never mounts it — nothing is server-rendered that was, and the chunk no longer carries the
// words. Where the flag IS on, the overlay now appears on the first client commit instead of in the
// server HTML; the element is created only inside `ageGateOpen &&`, so a document that does not
// show it never suspends on the import at all.
const AgeGate = lazy(() => import('@product/ovid/components/AgeGate/AgeGate').then((m) => ({default: m.AgeGate})))
// The veiled-wall host's post-sign-in age+terms card (plan 2026_09_17_ovid_veiled_wall.md, step 7).
// Statically imported for the same reason as AgeGate above — an SSR-rendered overlay for a
// signed-in visitor with no prior acceptance needs no client round-trip to appear, and every
// dependency here is already in every bundle (React, react-router's Link, ovid analytics).
import {AgeTermsGate} from '@core/consent/AgeTermsGate'
import ageTermsGateStyles from '@product/ovid/components/AgeTermsGate/AgeTermsGate.module.scss'
import {writeAccepted} from '@core/consent/storage'
import {detectLangFromPath} from '@product/quies/lib/hreflang'

// Load product root dynamically
const productId = getProductId()
const productConfig = getProductConfig()

type ENV = {
    ENV: string
    BUILD_VERSION: string
    GA_MEASUREMENT_ID?: string
    PRODUCT_NAME: string
    /** Read by `entry.client.tsx`, pre-hydration, to seed `traffic_type: 'internal'` before the
     *  first GA4 hit of an internal (founder) page load (decision 7, `2026_09_21_wall_gaps.md`) —
     *  the root `useEffect` that used to be the only writer runs after the first commit's own
     *  child events (a `wall_search`, a `gate_hit`, …), so those hits shipped with no internal
     *  flag and reached the "Internal Traffic" GA4 ingestion filter untouched. */
    isInternalUser?: boolean
}

declare global {
    var ENV: ENV

    interface Window {
        ENV: ENV
    }
}

export async function loader({request}: LoaderFunctionArgs) {
    // Dynamic imports to avoid bundling server-only code in client
    const {getProductFunnel} = await import('@core/products/registry')
    const config = await productConfig.config()
    const funnel = await getProductFunnel()

    const env = {
        ENV: process.env.ENV || 'production',
        BUILD_VERSION: process.env.BUILD_VERSION || 'unknown',
        GA_MEASUREMENT_ID: process.env.GA_MEASUREMENT_ID,
        PRODUCT_NAME: productId,
    } satisfies ENV

    // Ovid now has a full auth surface (Google OIDC + magic-link routes under
    // products/ovid/routes/). Session handling runs for all products; the
    // narcis-only studio/swap reads below stay gated.
    const {handleSession} = await import('@core/auth/auth.server')
    const {commitSession} = await import('@core/auth/session.server')
    const {session, user, tokens} = await handleSession(request)
    const {publicSignIn} = await import('@core/auth/signInMethods.server')
    const signIn = await publicSignIn()

    // _ga client-id capture (narcis_funnel plan, Architecture decision 7) — every product, not
    // just narcis: the session key and the capture cost are the same regardless, and it costs
    // quies/ovid nothing to carry a value their own funnel work doesn't read yet. Only written
    // when it actually changes, so a returning visitor with an unchanged `_ga` cookie doesn't
    // dirty the session on every page view beyond what already happens below (magicLinkNonce).
    const {parseGaClientId} = await import('@core/utils/gaCookie.server')
    const gaClientId = parseGaClientId(request.headers.get('cookie'))
    if (gaClientId && session.get('gaClientId') !== gaClientId) {
        session.set('gaClientId', gaClientId)
    }

    // Magic-link nonce — minted once here (the one root every product shares) and read by
    // MagicLinkForm/SignInCard wherever they mount: product login pages AND every SignupGate
    // site (pack pages, portrait, swap, model). Idempotent, not rotated on every render: the
    // login-page loaders used to mint per-page-load, which meant SignupGate sites — which never
    // minted at all — always POSTed with no nonce and were rejected by magic-link.server.ts's
    // check. Keeping the existing stored nonce (until it's within 5 minutes of the server's
    // 60-minute validity window) also avoids breaking two-tab and back-forward-cache flows,
    // which per-render rotation would have caused even for the pages that did mint.
    const {randomBytes} = await import('crypto')
    const storedNonce = session.get('magicLinkNonce') as {value: string; mintedAt: number} | undefined
    const magicLinkNonce = storedNonce && Date.now() - storedNonce.mintedAt <= 55 * 60 * 1000
        ? storedNonce.value
        : randomBytes(16).toString('hex')
    if (!storedNonce || magicLinkNonce !== storedNonce.value) {
        session.set('magicLinkNonce', {value: magicLinkNonce, mintedAt: Date.now()})
    }

    // What the narcis model intake asks at Start (pay at launch): the payment, or nothing again
    // for a payment not yet consumed. Null for other products.
    // swapCount and freeSwaps are narcis-only session-level props used by UserBlock anon chip.
    let modelPayment: 'paid' | 'pay' | null = null
    // Ovid's reading of the same question: what the next Start asks to be paid (`startAsksFor`).
    let ovidStartAsks: 'plan' | 'muse' | null = null
    // Whether the user holds a plan in force (ovid, quies): the account menu's way to the billing portal.
    let hasPlan = false
    // The currency the price is written in for this viewer (narcis and ovid).
    let modelCurrency: 'eur' | 'usd' = 'usd'
    let swapCount = 0
    let freeSwaps = 5
    // The user's trained model — minimal status only (no thumbUrls/photoCount; UserBlock's
    // dropdown entry just needs the badge text). THIS is the loader UserBlock actually reads
    // (`useRouteLoaderData('root')` resolves to this mounted root — the per-product
    // app/products/narcis/root.tsx that once shadowed this was dead code and was deleted
    // 2026-07-16; the userModelStatus fetch lived there first and never ran, 2026-07-13 incident).
    // Narcis only: ovid holds several models per account (its own muses), read where they are shown
    // (`/muse`, the wall's strip), so its visitor state stays 'anon' | 'signed'.
    // `delivered` (narcis_funnel plan, step 4; funnel spec §6) drives the header avatar dot +
    // the dropdown item's "· ready — see its first looks" vs "· ready" tail — only meaningful
    // while status === 'ready', absent otherwise (mirrors `percent`'s own training-only gate).
    let userModelStatus: {status: 'training' | 'ready' | 'failed'; percent?: number; delivered?: boolean} | null = null
    // visitor_state (narcis_funnel plan, Architecture decision 1) — the funnel's one axis,
    // derived here once and exposed on loader data for every route. GA4 user-property wiring
    // is step 2 of that plan; this loader intentionally stops at exposing the value — see
    // visitorState.server.ts's header comment for the seam. Defaults to 'anon' for products
    // with no training-shift surface (quies) and for any DB-error path below (same fail-open
    // posture as userModelStatus).
    let visitorState: 'anon' | 'signed' | 'training' | 'owner' = 'anon'

    if (productId === 'narcis') {
        const userId = (session.data as Record<string, unknown>)?.userId as string | undefined
        if (userId) {
            try {
                const {getUserModel} = await import('@core/products/trainingShift.server')
                const model = await getUserModel(userId)
                userModelStatus = model ? {status: model.status, percent: model.percent, delivered: model.delivered} : null
            } catch {
                // Non-fatal: dropdown degrades to no "Your model" entry on DB error.
            }
        }
        const {deriveVisitorState} = await import('@core/products/visitorState.server')
        visitorState = deriveVisitorState(userId, userModelStatus)
    } else if (productId === 'ovid' || productId === 'toile') {
        // visitorState still needs to distinguish anon/signed — for ovid, the muse UI
        // (the hub's queue affordance, the muse band); for toile, the wall's own sign-in surface
        // (2026-08-30/31, `thinking/plans/2026_08_31_wall_plan_e_toile_page_to_wall.md`). Neither
        // ever reaches 'training'/'owner' — toile has no training/model surface at all (plan
        // `2026_09_02_toile_funnel_core_tooling.md` §4).
        const userId = (session.data as Record<string, unknown>)?.userId as string | undefined
        visitorState = userId ? 'signed' : 'anon'
    }

    // Ovid muses (thinking/plans/2026_08_23_ovid_muses.md) — the chosen casting clause, read once
    // here so every route's Header/UserBlock can show her without its own query. `null` covers
    // both "no muse chosen" and a stale slug that no longer resolves in the registry (getMuse's
    // own null-return path — see muses/registry.ts). Ovid-only; narcis/quies carry no
    // `ovidMuseSlug` column read.
    let ovidChosenMuse: {slug: string; name: string; thumb: string | null} | null = null
    // `tosAcceptedAt` read alongside the muse query (same row, same userId guard) rather than a
    // second query — the veiled-wall post-sign-in gate's own condition
    // (`core/consent/postSignInGate.ts`) needs it below. `null` for an anonymous visitor, same as
    // `ovidChosenMuse`.
    let ovidTosAcceptedAt: Date | null = null
    if (productId === 'ovid') {
        const userId = (session.data as Record<string, unknown>)?.userId as string | undefined
        if (userId) {
            try {
                const {prisma} = await import('@core/database/prisma.server')
                const row = await prisma.user.findUnique({where: {id: userId}, select: {tosAcceptedAt: true}})
                ovidTosAcceptedAt = row?.tosAcceptedAt ?? null
                const {resolveChosenMuse, summaryOf} = await import('@product/ovid/muses/ownMuses.server')
                const muse = await resolveChosenMuse(userId)
                if (muse) ovidChosenMuse = summaryOf(muse)
            } catch {
                // Non-fatal: Header degrades to the no-muse ambient state on DB error; the
                // post-sign-in gate below degrades to "don't show" on the same error (fail-open,
                // matching every other read in this loader).
            }
        }
    }

    const startUserId = (session.data as Record<string, unknown>)?.userId as string | undefined
    // Narcis: the purchase ledger of its model intake, which asks payment at Start.
    if (productId === 'narcis') {
        modelPayment = 'pay'
        if (startUserId) {
            try {
                const {openPurchaseFor} = await import('@product/narcis/services/modelPurchase.server')
                if (await openPurchaseFor(startUserId)) modelPayment = 'paid'
            } catch {
                // Non-fatal: the intake offers the checkout, which refuses a second payment itself.
            }
        }
    }
    // Ovid: what the next Start asks to be paid — the plan, an extra muse, or nothing.
    if (productId === 'ovid') {
        ovidStartAsks = 'plan'
        modelCurrency = (await import('@core/billing/displayCurrency')).displayCurrency(request)
        try {
            const {startAsksFor} = await import('@product/ovid/services/purchase.server')
            ovidStartAsks = await startAsksFor(startUserId)
        } catch {
            // Non-fatal: the intake offers the plan, and Start answers what it really asks.
        }
    }
    if (productId === 'ovid' || productId === 'quies') {
        try {
            const {hasActivePlan} = await import('@core/billing/planState.server')
            hasPlan = await hasActivePlan(startUserId, productId)
        } catch {
            // Non-fatal: the menu leaves out the way to the portal.
        }
    }

    if (productId === 'narcis') {
        modelCurrency = (await import('@core/billing/displayCurrency')).displayCurrency(request)
        swapCount = (session.data as Record<string, unknown>)?.swapCount as number ?? 0
        const {narcisConfig} = await import('@product/narcis/config')
        freeSwaps = narcisConfig.freeSwaps
    }

    // GA4 identity. Both the email hash and the operator allowlist are computed in a
    // .server module so they never enter the client bundle or its public sourcemap.
    const {analyticsIdentity, isInternalEmail} = await import('@core/utils/analytics.server')
    // Computed once, read by both the loader's own `isInternalUser` field (root's `useEffect`,
    // for a mid-session sign-in/out change) and `env` below (the pre-hydration inline script
    // `entry.client.tsx` reads to seed `traffic_type` ahead of the first commit's own events —
    // decision 7, `2026_09_21_wall_gaps.md`) — one email check, not two.
    const isInternalUser = isInternalEmail(user?.email)

    // Ovid 18+ entry gate (plan thinking/plans/2026_08_05_ovid_age_gate.md, step 2). The whole
    // decision is one pure function so the ovid × cookie × bot-UA × exempt-path matrix is
    // testable without standing up this loader's session and DB reads.
    //
    // The overlay is parked (plan 2026_09_17_ovid_veiled_wall.md, step 7): it shows only under
    // `OVID_AGE_OVERLAY_AT_ENTRY=on`. `ageOverlayAtEntry` folds into `shouldShowAgeGate` so the
    // flag×cookie×bot×path matrix stays one function; the config is ovid's own, so only ovid
    // imports it.
    const ageOverlayAtEntry = productId === 'ovid'
        ? (await import('@product/ovid/config')).OVID_AGE_OVERLAY_AT_ENTRY
        : false
    const {hasAgeAck, shouldShowAgeGate} = await import('@core/utils/ageGateCookie.server')
    const showAgeGate = shouldShowAgeGate({
        productId,
        hasAck: await hasAgeAck(request),
        userAgent: request.headers.get('user-agent'),
        pathname: new URL(request.url).pathname,
        overlayAtEntry: ageOverlayAtEntry,
    })

    // The post-sign-in age+terms card (same plan, same step) — the entry overlay's replacement on
    // this host. Shows once, for a signed-in user whose account has not yet accepted; `root.tsx`
    // App() below turns this server truth into the SSR-rendered overlay, same posture as
    // `showAgeGate`/`ageGateOpen`.
    const {shouldShowPostSignInGate, shouldSeedBrowserTos} = await import('@core/consent/postSignInGate')
    const showPostSignInGate = shouldShowPostSignInGate({
        productId,
        ageOverlayAtEntry,
        userId: (session.data as Record<string, unknown>)?.userId as string | undefined,
        tosAcceptedAt: ovidTosAcceptedAt,
    })
    // Consent coherence (wall verbs integration pass, 2026-09-17): an account that accepted on
    // ANOTHER device carries the DB stamp but not THIS browser's own `ovid_tos_accepted` flag —
    // `showPostSignInGate` above correctly stays closed for it, but the wall's in-column
    // `TosConsentCard` reads the browser flag alone and would still ask. `App()`'s mount effect
    // seeds that flag from this same DB read when true, so the in-column card never opens for an
    // account this loader already knows has accepted.
    const seedBrowserTos = shouldSeedBrowserTos({productId, tosAcceptedAt: ovidTosAcceptedAt})

    return json(
        {
            ENV: {...env, isInternalUser}, user, tokens, config: toPublicConfig(config), funnel, modelPayment, ovidStartAsks, hasPlan, modelCurrency, swapCount, freeSwaps, userModelStatus,
            visitorState, showAgeGate, showPostSignInGate, seedBrowserTos, ovidChosenMuse,
            isInternalUser,
            analytics: analyticsIdentity(user),
            dbSessionId: (session.data as Record<string, unknown>)?.dbSessionId ?? null,
            magicLinkNonce,
            signIn,
        },
        {headers: {'Set-Cookie': await commitSession(session)}},
    )
}

// Meta and links come from product config
export const meta: MetaFunction<typeof loader> = ({data}) => {
    if (!data) {
        return [{title: 'Error'}]
    }

    const {config} = data
    const meta = config.meta as typeof config.meta & {
        ogImageType?: string
        ogImageWidth?: number
        ogImageHeight?: number
    }
    const ogImageType = meta.ogImageType ?? 'image/jpeg'
    const ogImageDimensions = meta.ogImageWidth && meta.ogImageHeight
        ? [
            {property: 'og:image:width', content: String(meta.ogImageWidth)},
            {property: 'og:image:height', content: String(meta.ogImageHeight)},
        ]
        : []

    return [
        {title: config.meta.title},
        {name: 'description', content: config.meta.description},
        {name: 'keywords', content: config.meta.keywords},
        {name: 'language', content: 'en'},
        {property: 'og:type', content: 'website'},
        {property: 'og:title', content: config.name},
        {property: 'og:site_name', content: config.domain},
        {property: 'og:description', content: config.meta.description},
        {property: 'og:image', content: config.meta.ogImage},
        {property: 'og:image:secure_url', content: config.meta.ogImage},
        {property: 'og:url', content: config.meta.url},
        {property: 'og:image:type', content: ogImageType},
        ...ogImageDimensions,
        {property: 'twitter:card', content: 'summary_large_image'},
        {property: 'twitter:url', content: config.meta.url},
        // No twitter:title or twitter:description here: X falls back to og:title and og:description, which a page
        // overrides. A root-level twitter:description outlived every page's own og:description (quies, 2026-11-01).
        {property: 'twitter:image', content: config.meta.ogImage},
        {name: 'theme-color', content: config.meta.themeColor},
        {name: 'msapplication-TileColor', content: config.meta.themeColor},
        {name: 'color-scheme', content: config.meta.colorScheme},
    ]
}

// Product-specific icon paths — all link tags route directly to product-prefixed assets.
// The only exception is /favicon.ico which user agents fetch directly without consulting
// link tags; build-product.sh overwrites it per-product at build time.
const ICON_SETS = {
    narcis: {
        svg: '/narcis-favicon.svg',
        altPng: '/narcis-favicon-48x48.png',
        appleTouch: '/narcis-apple-touch-icon.png',
        manifest: '/site-narcis.webmanifest',
    },
    quies: {
        svg: '/quies-favicon.svg',
        altPng: '/quies-favicon-48x48.png',
        appleTouch: '/quies-apple-touch-icon.png',
        manifest: '/site-quies.webmanifest',
    },
    ovid: {
        svg: '/ovid-favicon.svg',
        altPng: '/ovid-favicon-48x48.png',
        appleTouch: '/ovid-apple-touch-icon.png',
        manifest: '/site-ovid.webmanifest',
    },
    toile: {
        svg: '/toile-favicon.svg',
        altPng: '/toile-favicon-48x48.png',
        appleTouch: '/toile-apple-touch-icon.png',
        manifest: '/site-toile.webmanifest',
    },
} as const

export const links: LinksFunction = () => {
    const icons = ICON_SETS[productId as keyof typeof ICON_SETS] ?? ICON_SETS.narcis

    // Product-specific font links.
    // Narcis: Fraunces (display) + Inter (body) + JetBrains Mono, self-hosted
    //   (@font-face rules in products/narcis/styles/partials/fonts.scss) since the
    //   2026-07-18 LCP fix — was a fonts.googleapis.com <link rel="stylesheet">, which is
    //   render-blocking (CSSOM construction, hence first paint, waits on it) and measured
    //   as the dominant contributor to narcis's mobile LCP element-render delay. Only the
    //   two above-the-fold critical faces are preloaded here — AnimatedWall's hero
    //   headline (Fraunces 500 italic) and subtitle/eyebrow (Inter 400) — everything else
    //   in the family set loads on first use via the bundled @font-face rules.
    // Quies: EB Garamond italic (display) + Jost (text), self-hosted for the same reason
    //   (plan 3 phase A). Both files are single variable fonts, so both preload. Noto is not
    //   loaded: CJK, Arabic and Devanagari fall back to whatever the visitor's OS has.
    // Ovid: Archivo (grotesque UI/body/labels), self-hosted for the same reason as narcis
    //   (@font-face rules in products/ovid/styles/index.scss). Cormorant Garamond (House's
    //   display face, replacing Bodoni Moda) was already self-hosted before this fix.
    //   Preloads the one above-the-fold weight — heroSub body copy (Archivo 400).
    const fontLinks: Array<{rel: string; href: string; crossOrigin?: 'anonymous' | 'use-credentials'; as?: string; type?: string}> =
        productId === 'quies' ? [
            {rel: 'preload', href: '/fonts/eb-garamond-italic-var.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
            {rel: 'preload', href: '/fonts/jost-var.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
        ]
        : productId === 'narcis' ? [
            {rel: 'preload', href: '/fonts/Fraunces-500-Italic.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
            {rel: 'preload', href: '/fonts/Inter-400-Regular.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
        ]
        : productId === 'ovid' ? [
            {rel: 'preload', href: '/fonts/Archivo-400-Regular.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
        ]
        // Toile: Archivo only (500 display/wordmark, 400 body), self-hosted in
        //   products/toile/styles/index.scss for the same render-blocking reason as narcis and
        //   ovid. Preloads the one above-the-fold weight — the wordmark is Archivo 500 and it
        //   is the landing's LCP element.
        : productId === 'toile' ? [
            {rel: 'preload', href: '/fonts/Archivo-500-Regular.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous'},
        ]
        : []

    // Ovid's icon tag shape follows the House redesign spec exactly (plain `icon` rel with
    // an explicit `sizes` on the PNG, no `alternate icon`). Narcis/quies keep the original
    // shared shape untouched — byte-identical output for those two products is required.
    const iconLinks = productId === 'ovid'
        ? [
            {rel: 'icon', type: 'image/svg+xml', href: icons.svg},
            {rel: 'icon', type: 'image/png', sizes: '48x48', href: icons.altPng},
            {rel: 'shortcut icon', href: '/favicon.ico'},
            {rel: 'apple-touch-icon', href: icons.appleTouch},
            {rel: 'manifest', href: icons.manifest, crossOrigin: 'use-credentials'} as const,
        ]
        : [
            {rel: 'alternate icon', type: 'image/png', href: icons.altPng},
            {rel: 'icon', type: 'image/svg+xml', href: icons.svg},
            {rel: 'shortcut icon', href: '/favicon.ico'},
            {rel: 'apple-touch-icon', href: icons.appleTouch},
            {rel: 'manifest', href: icons.manifest, crossOrigin: 'use-credentials'} as const,
        ]

    return [
        ...fontLinks,
        ...iconLinks,
    ]
}

type DocumentProps = PropsWithChildren

// Quies pages localize by path (`/fr` also answers as `/FR`); the session page starts at 'en'
// and sets its own language once chosen.
function htmlLangFor(pathname: string): string {
    return productId === 'quies' ? detectLangFromPath(pathname.toLowerCase()) : 'en'
}

function Document({children}: DocumentProps) {
    const location = useLocation()
    return (
        <html lang={htmlLangFor(location.pathname)} data-product={productId}>
        <head>
            <meta charSet="utf-8"/>
            <meta name="viewport" content="width=device-width,initial-scale=1"/>
            <Meta/>
            <Links/>
        </head>
        <body>
        {children}
        <ScrollRestoration/>
        <Scripts/>
        <script src="https://js.stripe.com/v3" async></script>
        </body>
        </html>
    )
}

export function ErrorBoundary() {
    const error = useRouteError()
    const is404 = isRouteErrorResponse(error) && error.status === 404

    // Narcis gets the canon-token error page (design canvas 04, thinking/design/handoffs/
    // training_shift/). This is the ONLY ErrorBoundary that renders in production — the
    // per-product app/products/narcis/root.tsx once exported one too, but that component was
    // never wired into the registry (only its `loader` type was imported elsewhere for typing);
    // the dead file was deleted 2026-07-16. Narcis's index.scss is imported unconditionally above (line 22)
    // and its Google Fonts link is emitted whenever PRODUCT_NAME=narcis (links(), above), so
    // both the --bg/--yolk/... custom properties and Fraunces/JetBrains Mono are reliably
    // present here — safe to style with token vars instead of hard-coded hex.
    if (productId === 'narcis') {
        const statusCode = isRouteErrorResponse(error) ? error.status : 500
        return (
            <Document>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    padding: '60px 32px',
                    textAlign: 'center',
                    gap: '6px',
                    background: 'var(--bg)',
                }}>
                    <svg
                        width="34"
                        height="34"
                        viewBox="0 0 64 64"
                        fill="none"
                        aria-hidden="true"
                        style={{marginBottom: '18px'}}
                    >
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9"/>
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(60 32 32)"/>
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(120 32 32)"/>
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(180 32 32)"/>
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(240 32 32)"/>
                        <ellipse cx="32" cy="14" rx="6.5" ry="14" fill="#FAFAF9" transform="rotate(300 32 32)"/>
                        <circle cx="32" cy="32" r="9" fill="#E8C24A"/>
                        <circle cx="32" cy="32" r="9" fill="none" stroke="#FAFAF9" strokeWidth="0.8"/>
                    </svg>

                    <p style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '.18em',
                        color: 'var(--text-dim)',
                        margin: '0 0 10px',
                    }}>
                        {is404 ? '404' : `error ${statusCode}`}
                    </p>

                    <h1 style={{
                        fontFamily: 'var(--font-display)',
                        fontStyle: 'italic',
                        fontWeight: 500,
                        fontSize: '44px',
                        color: 'var(--text)',
                        margin: '0 0 10px',
                    }}>
                        {is404 ? "This page doesn't exist." : 'Something broke — on our side.'}
                    </h1>

                    <p style={{
                        fontSize: '15px',
                        color: 'var(--text-muted)',
                        margin: '0 0 26px',
                        maxWidth: '46ch',
                        lineHeight: 1.6,
                    }}>
                        {is404
                            ? "The link may be old, or the pack may have moved. Everything you've made is safe in your account."
                            : 'Try again in a moment. Nothing you made was lost.'}
                    </p>

                    <Link
                        to={routerPaths['/']}
                        style={{
                            color: 'var(--yolk)',
                            textDecoration: 'none',
                            fontSize: '15px',
                        }}
                    >
                        ← Back home
                    </Link>
                </div>
            </Document>
        )
    }

    return (
        <Document>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '24px',
                textAlign: 'center',
                background: '#0f0f0f',
            }}>
                <div style={{
                    fontSize: '3.5rem',
                    fontWeight: 200,
                    color: 'rgba(255, 255, 255, 0.15)',
                    marginBottom: '16px',
                }}>
                    {is404 ? '404' : '500'}
                </div>

                <h1 style={{
                    fontSize: '1.5rem',
                    fontWeight: 400,
                    color: '#ffffff',
                    marginBottom: '12px',
                }}>
                    {is404
                        ? 'Page not found'
                        : 'Something went wrong'}
                </h1>

                <p style={{
                    color: 'rgba(185, 185, 185, 0.8)',
                    fontSize: '0.95rem',
                    marginBottom: '32px',
                    maxWidth: '360px',
                    lineHeight: 1.6,
                }}>
                    {is404
                        ? 'The page you are looking for does not exist.'
                        : 'An unexpected error occurred. Please try again.'}
                </p>

                {isRouteErrorResponse(error) && !is404 && (
                    <p style={{
                        color: 'rgba(185, 185, 185, 0.4)',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        marginBottom: '24px',
                    }}>
                        Error {error.status}
                    </p>
                )}

                <Link
                    to={routerPaths['/']}
                    style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        textDecoration: 'none',
                        fontSize: '0.9rem',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                        paddingBottom: '2px',
                    }}
                >
                    Go back
                </Link>
            </div>
        </Document>
    )
}

// Maps the free-form `trigger` string SignupGate call sites already pass (SignInOptions/
// MagicLinkForm's own `trigger` prop — e.g. 'pack-checkout', 'model_intake', 'swap', 'login-page')
// onto the narcis_funnel dictionary's closed sign_up trigger enum (funnel spec §2:
// `train_gate|tool_gate|direct`). Lives here (shared root.tsx), not in narcis's own analytics
// module, because sign_up fires through THIS file's `track()` call for every product — see
// CoreEventMap.sign_up's own header comment. Ovid posts this same `trigger` param too (its
// auth.login.tsx forwards `gate-trigger` into a hidden `trigger` form field on both sign-in
// forms, as of 153ee6f7); its own values ('model_intake', 'muse', …) still fall through to
// 'direct' below since the enum has no bucket for them yet — that mapping gap, not a missing
// param, is why ovid mostly reads 'direct' today. Quies never populates the param at all, so it
// always falls through to 'direct', a harmless default.
// The wall's own triggers pass through unmapped: `wall` (the first Generate by a visitor,
// `GATE_TRIGGER_WALL` in @core/wall/contract) and `page_verb` (a pack page's one-shot verb) name
// gates the funnel enum has no bucket for, and folding them into 'direct' would erase the one
// signal the wall's sign-in gate produces (wall plan d, review item S11). One Tap passes through for
// the same reason: folded into 'direct' it reads as a press of the login page's Google button.
function mapAuthTrigger(raw: string | null): 'train_gate' | 'tool_gate' | 'direct' | 'wall' | 'page_verb' | 'share' | 'one-tap' {
    if (raw === 'swap') return 'tool_gate'
    if (raw === 'pack-checkout' || raw === 'model_intake') return 'train_gate'
    if (raw === GATE_TRIGGER_WALL || raw === 'page_verb' || raw === GATE_TRIGGER_SHARE || raw === GATE_TRIGGER_ONE_TAP) return raw
    return 'direct'
}

function App() {
    const {ENV, isInternalUser, analytics, dbSessionId, visitorState, showAgeGate, showPostSignInGate, seedBrowserTos, user, signIn} = useLoaderData<typeof loader>()
    const location = useLocation()
    // Server truth AND the local acknowledgment, not either alone: the loader flag gives the
    // first render its answer (no flash for a returning visitor, no hydration delay for a
    // first-time one) and keeps answering across client navigations (an exempt path revalidates
    // to false); `ageAcknowledged` removes the overlay on the Enter click, before the cookie it
    // just wrote has any next request to be read on.
    const [ageAcknowledged, setAgeAcknowledged] = useState(false)
    const ageGateOpen = showAgeGate && !ageAcknowledged

    // The veiled-wall host's post-sign-in age+terms card (plan 2026_09_17_ovid_veiled_wall.md,
    // step 7) — same local-acknowledgment pattern as the age gate above, so Continue removes the
    // overlay immediately rather than waiting on the next full navigation to see the stamped row.
    const [postSignInAcknowledged, setPostSignInAcknowledged] = useState(false)
    const postSignInGateOpen = showPostSignInGate && !postSignInAcknowledged

    // Consent coherence (wall verbs integration pass, 2026-09-17) — seeds this browser's own
    // `ovid_tos_accepted` flag (`core/consent/storage.ts`) from the account's DB stamp when the
    // loader already knows it accepted (`shouldSeedBrowserTos`), so the wall's in-column
    // `TosConsentCard` — gated on that flag alone via `useConsentGate` — never re-asks an account
    // that accepted on another device. `writeAccepted` is idempotent (a plain localStorage set), so
    // running this on every mount where the loader says true is harmless; the `[seedBrowserTos]`
    // dependency keeps it from re-writing on every unrelated re-render.
    useEffect(() => {
        if (seedBrowserTos) writeAccepted('ovid')
    }, [seedBrowserTos])
    async function handlePostSignInAccept() {
        writeAccepted('ovid')
        try {
            await fetch('/consent/accept', {method: 'POST'})
        } catch {
            // Non-fatal: the browser flag above already covers this tab/session; a failed stamp
            // just means the account is asked again at the next sign-in, on this device or another.
        }
        setPostSignInAcknowledged(true)
    }

    // GA4 initialize() moved to entry.client.tsx (2026-08-25) — synchronous, before hydrateRoot, so
    // it runs ahead of any first-commit child effect that fires a track() call (AgeGate's
    // age_gate_shown, gate_hit on a direct gated load, etc.). A root useEffect here ran too late:
    // React commits child effects before parent effects, so those child mounts could call track()
    // before this effect had initialized GA. See entry.client.tsx's own comment for the mechanism,
    // the measured defect (ovid age_gate_passed 307 vs age_gate_shown 0, Aug 4-25), and why
    // send_page_view: false is load-bearing — that rationale moved there verbatim.
    //
    // Decision 7 (`2026_09_21_wall_gaps.md`): the FIRST hit of an internal load is now seeded by
    // `entry.client.tsx`, synchronously alongside `initialize()`, from `window.ENV.isInternalUser`
    // — the same pre-hydration timing fix this file's own comment above already applies to GA4
    // init itself, since this effect runs after the first commit's own child events (`wall_search`,
    // a direct-load `gate_hit`, …) exactly like the initialize-timing defect measured 2026-08-25.
    // This effect stays for what the inline script can't cover: a sign-in or sign-out MID-session,
    // which changes `isInternalUser` without a fresh document load to re-run the inline script.
    useEffect(() => {
        if (isInternalUser) markInternalTraffic()
    }, [isInternalUser])

    useEffect(() => {
        setAnalyticsIdentity(analytics.personId, analytics.dbUserId)
    }, [analytics.personId, analytics.dbUserId])

    // GA4 <-> DB session bridge for anonymous visitors: carry the Session.id cuid as the
    // browser_session_id user property, the same vehicle setAnalyticsIdentity uses for db_user_id.
    // See setBrowserSessionId (@core/utils/analytics) for the mechanism and why this exists
    // alongside setAnalyticsIdentity rather than folding into it.
    useEffect(() => {
        setBrowserSessionId((dbSessionId as string | null) ?? null)
    }, [dbSessionId])

    // visitor_state (narcis_funnel plan, Architecture decision 1 / step 2) — the funnel's one
    // user property, set on every hit. Re-fires when the value changes (sign-in, training
    // completion) via loader revalidation, same as the identity effects above.
    useEffect(() => {
        setVisitorState(visitorState)
    }, [visitorState])

    // Track page views on route changes
    useEffect(() => {
        trackPageView(location.pathname + location.search)
    }, [location])

    // Detect auth and purchase events from URL params added by server redirects
    useEffect(() => {
        const params = new URLSearchParams(location.search)

        const authEvent = params.get('auth-event')
        const authMethod = params.get('auth-method') as 'google' | 'facebook' | 'email' | null

        if (authEvent === 'signup' || authEvent === 'signin') {
            // 'magic_link' is the narcis_funnel dictionary's own vocabulary (funnel spec §2:
            // `sign_up(method: google|magic_link, ...)`) — narcis-only so quies/ovid's existing
            // 'email' value (and any historical GA4 data using it) is undisturbed. productId is
            // resolved once at module scope (getProductId(), same as the rest of this file), not
            // a per-product import, so this stays inside the @core/@product boundary.
            const method = authMethod === 'google' || authMethod === 'facebook' ? authMethod : productId === 'narcis' ? 'magic_link' : 'email'
            if (authEvent === 'signup') {
                // sign_up (funnel spec §2) — re-shaped from the old {method} shape to carry
                // `trigger` too (narcis_funnel plan, step 2). trigger comes from the same URL
                // param the OAuth callback / magic-link verify append (see auth.server.ts's
                // setAuthReturn/popAuthTrigger and magic-link.server.ts/-verify.server.ts),
                // mapped onto the dictionary's closed enum by mapAuthTrigger above.
                track('sign_up', {method, trigger: mapAuthTrigger(params.get('trigger'))})
            } else {
                // `sign_in` reads the trigger from the same URL param `sign_up` does — the two
                // sides of one gate, and a returning user is the common case at the wall.
                track('sign_in', {method, trigger: mapAuthTrigger(params.get('trigger'))})
            }
        }

        const purchase = params.get('purchase')
        if (purchase === 'success') {
            // A pack checkout's return carries pack_slug/value/currency on this URL for GA4.
            // CoreEventMap's `purchase_complete` has no `pack_slug` field (`price_id` was written
            // for a Stripe price id, which never reaches this URL); price_id carries pack_slug, the
            // closest identifying value here, rather than the literal 'unknown'. tokens has no pack
            // analogue and stays 0.
            const packSlug = params.get('pack_slug')
            const value = Number(params.get('value'))
            const currency = params.get('currency')
            track('purchase_complete', {
                price_id: packSlug ?? 'unknown',
                value: Number.isFinite(value) ? value : 0,
                currency: currency ?? 'EUR',
                tokens: 0,
            })
        } else if (purchase === 'cancelled') {
            // Same price_id substitution as purchase_complete above; purchase_failed has no
            // value/currency fields to read regardless. `error` stays unset — a Stripe Checkout
            // cancel is a user bounce, not a reported error, and the cancel URL carries none.
            const packSlug = params.get('pack_slug')
            track('purchase_failed', {price_id: packSlug ?? 'unknown'})
        }

        // auth-error is consumed by SignInCard at /auth/login but must also be stripped here
        // if a signed-in user somehow reaches / with the param (e.g. transient DB error path).
        const authError = params.get('auth-error')

        if (authEvent || purchase || authError) {
            // Built from the LIVE URL, not the router's `location.search` snapshot — a child route
            // may already have stripped its own return-token params from the address bar in its own
            // effect by the time this one runs, and replaying the snapshot would put those keys
            // back. `authEvent`/`purchase`/`authError` above keep reading the snapshot: they are
            // what the server sent this page, which is what this effect reports, whether or not a
            // child has already taken them off the address bar.
            const clean = new URLSearchParams(window.location.search)
            clean.delete('auth-event')
            clean.delete('auth-method')
            clean.delete('trigger')
            clean.delete('purchase')
            clean.delete('auth-error')
            const qs = clean.toString()
            window.history.replaceState(window.history.state, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash)
        }
    }, [location.search])

    useBehavioralTracking()

    return (
        <Document>
            <Outlet/>
            {/* Behind the age gate, never over it: the prompt waits until the visitor is let in. */}
            {!user && signIn.googleOneTapClientId && !ageGateOpen && <GoogleOneTap clientId={signIn.googleOneTapClientId}/>}
            {/* Rendered AFTER the outlet, never in place of it — the page content is present in
                the DOM for every visitor and the gate only covers it (plan decision 1). */}
            {ageGateOpen && (
                <Suspense fallback={null}>
                    <AgeGate onAcknowledged={() => setAgeAcknowledged(true)}/>
                </Suspense>
            )}
            {postSignInGateOpen && (
                <AgeTermsGate
                    styles={ageTermsGateStyles}
                    termsHref="/terms"
                    privacyHref="/privacy"
                    onAccept={handlePostSignInAccept}
                    copy={{
                        title: 'For adults only.',
                        ageLabel: 'I am 18 or older.',
                        termsLabel: 'I accept the terms.',
                        termsLinkLabel: 'Terms',
                        privacyLinkLabel: 'Privacy',
                        button: 'Continue',
                    }}
                />
            )}
            <script
                dangerouslySetInnerHTML={{
                    __html: `window.ENV = ${JSON.stringify(ENV)}`,
                }}
            />
        </Document>
    )
}

export default App
